import { useState, useCallback } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { DeletedBreakdownPopover, PopoverLink, SkippedBreakdownPopover } from './components/breakdown-popovers';
import { DeletedFilesModal } from './components/modals/deleted-files-modal';
import { SkippedFilesModal } from './components/modals/skipped-files-modal';
import { UploadFlowChart } from './components/upload-flow-chart';
import { RunsProcessingSkeleton, StageCard, UploadOverviewSkeleton } from './components/upload-overview';
import { STAGE_CONFIGS, DEFAULT_STATS, StageConfig, getStageCount } from './utils';
import { hasOngoingRunWhichModifiesDocumentCount } from '../../../../../../helpers/runs';
import { useMatterProcessingStore } from '../../../../../stores/matter-processing-store';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { useGetFileHistoryStats } from '@/api/queries/useGetFileHistoryStats';
import type { FileHistoryStats } from '@/api/queries/useGetFileHistoryStats';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import type { File, SkippedFile, DeletionHistory, NotProcessedDoc, ProcessedDoc } from '@/api/types/backend-dto-types';
import useGetFetchConfig from '@/api/useGetFetchConfig';
import { downloadUploadOverviewReport } from '@/helpers/report-utils';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverTrigger } from '@/new/components/shadcn/popover';

export function Overview() {
  const { matterId } = useParams();
  const { fetchConfigGET } = useGetFetchConfig();
  const [isDownloading, setIsDownloading] = useState(false);
  const [skippedModalOpen, setSkippedModalOpen] = useState(false);
  const [deletedModalOpen, setDeletedModalOpen] = useState(false);

  const { data: stats, isLoading } = useGetFileHistoryStats(matterId ?? '');
  const { data: matter } = useGetMatterV2(matterId ?? '');
  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? undefined;
  const resolvedStats = stats ?? DEFAULT_STATS;

  const processingState = useMatterProcessingStore((state) =>
    matterId ? state.getProcessingState(matterId) : undefined,
  );
  const areRunsProcessing = hasOngoingRunWhichModifiesDocumentCount(processingState?.allProcessingRuns);

  const handleDownloadReport = useCallback(async () => {
    if (!matterId || resolvedStats.totalAttempted === 0) return;

    trackCustomEvent(TrackingEventsV2.FileHistoryDownloadOverviewReport, { matterId });
    setIsDownloading(true);
    try {
      const [skippedRes, deletedRes, awaitingRes, processedRes, notProcessedRes] = await Promise.all([
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/skipped`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/deleted`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/files`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/processed`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/not-processed`, fetchConfigGET),
      ]);

      const [skippedFiles, deletedFiles, awaitingFiles, processedDocs, notProcessedDocs] = await Promise.all([
        skippedRes.json() as Promise<SkippedFile[]>,
        deletedRes.json() as Promise<DeletionHistory[]>,
        awaitingRes.json() as Promise<File[]>,
        processedRes.json() as Promise<ProcessedDoc[]>,
        notProcessedRes.json() as Promise<NotProcessedDoc[]>,
      ]);

      downloadUploadOverviewReport(
        {
          stats: resolvedStats,
          skippedFiles,
          deletedFiles,
          awaitingFiles,
          processedDocs,
          notProcessedDocs,
        },
        matterId,
      );
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setIsDownloading(false);
    }
  }, [resolvedStats, matterId, fetchConfigGET]);

  if (!matterId) return null;
  if (areRunsProcessing) return <RunsProcessingSkeleton />;
  if (isLoading) return <UploadOverviewSkeleton />;

  function Stage({ config, stats, matterId }: { config: StageConfig; stats: FileHistoryStats; matterId: string }) {
    const count = getStageCount(stats, config.key);

    if (config.key === 'skipped') {
      return (
        <Popover>
          <PopoverTrigger asChild className="w-full text-left">
            <div
              className="w-full cursor-pointer text-left"
              onClick={() => trackCustomEvent(TrackingEventsV2.FileHistoryViewSkippedBreakdown, { matterId })}
            >
              <StageCard
                config={config}
                count={count}
                action={
                  <button className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                    View breakdown
                    <Icon className="lucide--chevron-down h-3 w-3" />
                  </button>
                }
              />
            </div>
          </PopoverTrigger>
          <SkippedBreakdownPopover
            stats={stats}
            align="start"
            onViewAll={() => {
              trackCustomEvent(TrackingEventsV2.FileHistoryOpenSkippedFilesModal, { matterId });
              setSkippedModalOpen(true);
            }}
          />
        </Popover>
      );
    }

    if (config.key === 'deleted') {
      return (
        <Popover>
          <PopoverTrigger asChild className="w-full text-left">
            <div
              className="w-full cursor-pointer text-left"
              onClick={() => trackCustomEvent(TrackingEventsV2.FileHistoryViewDeletedBreakdown, { matterId })}
            >
              <StageCard
                config={config}
                count={count}
                action={
                  <button className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                    View breakdown
                    <Icon className="lucide--chevron-down h-3 w-3" />
                  </button>
                }
              />
            </div>
          </PopoverTrigger>
          <DeletedBreakdownPopover
            stats={stats}
            align="start"
            onViewAll={() => {
              trackCustomEvent(TrackingEventsV2.FileHistoryOpenDeletedFilesModal, { matterId });
              setDeletedModalOpen(true);
            }}
          />
        </Popover>
      );
    }

    if (config.key === 'awaiting') {
      return (
        <StageCard
          config={config}
          count={count}
          action={
            <PopoverLink to={`/matters/${matterId}/docs/upload`} newTab>
              View files
            </PopoverLink>
          }
        />
      );
    }

    if (config.key === 'processed' && allDocumentsCaseId) {
      return (
        <StageCard
          config={config}
          count={count}
          action={
            <PopoverLink to={`/matters/${matterId}/subsets/${allDocumentsCaseId}/docs?processed=true`} newTab>
              View files
            </PopoverLink>
          }
        />
      );
    }

    if (config.key === 'notProcessed' && allDocumentsCaseId) {
      return (
        <StageCard
          config={config}
          count={count}
          action={
            <PopoverLink to={`/matters/${matterId}/subsets/${allDocumentsCaseId}/docs?processed=false`} newTab>
              View files
            </PopoverLink>
          }
        />
      );
    }

    return <StageCard config={config} count={count} />;
  }

  return (
    <>
      <SkippedFilesModal open={skippedModalOpen} onOpenChange={setSkippedModalOpen} matterId={matterId} />
      <DeletedFilesModal open={deletedModalOpen} onOpenChange={setDeletedModalOpen} matterId={matterId} />
      <div className="mb-12 rounded-md border border-gray-200">
        <div className="rounded-t-md border-b border-gray-200 bg-gray-50/80 px-4 py-2.5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-600">File Flow</h3>
            <Button variant="default" className="bg-white" size="sm" onClick={handleDownloadReport}>
              {isDownloading ? (
                <Icon className="lucide--loader-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Icon className="lucide--download h-3.5 w-3.5" />
              )}
              {isDownloading ? 'Downloading...' : 'Download Report'}
            </Button>
          </div>
        </div>

        <UploadFlowChart
          matterId={matterId}
          allDocumentsCaseId={allDocumentsCaseId}
          stats={resolvedStats}
          onViewSkipped={() => setSkippedModalOpen(true)}
          onViewDeleted={() => setDeletedModalOpen(true)}
        />

        <div className="border-t border-gray-100 px-4 py-4">
          <h4 className="mb-3 text-xs font-medium tracking-wide text-gray-400 uppercase">Stages</h4>
          <div className="space-y-4">
            <div>
              <Stage config={STAGE_CONFIGS.attempted} stats={resolvedStats} matterId={matterId} />
            </div>

            <div className="border-t border-gray-100 pt-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {[STAGE_CONFIGS.skipped, STAGE_CONFIGS.uploaded].map((config) => (
                  <Stage key={config.key} config={config} stats={resolvedStats} matterId={matterId} />
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  STAGE_CONFIGS.awaiting,
                  STAGE_CONFIGS.deleted,
                  STAGE_CONFIGS.processed,
                  STAGE_CONFIGS.notProcessed,
                ].map((config) => (
                  <Stage key={config.key} config={config} stats={resolvedStats} matterId={matterId} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
